import React, { useEffect, useState } from "react";
import Sidebar from "components/Sidebar/Sidebar.js";
import axios from "axios";
import { Spinner } from "components/Spinner";
import config from "services/config";
import { useAuth } from "context/auth/auth";
import {
  IconButton,
  TextField,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
} from "@mui/material";
import {
  Delete,
  Edit,
  Save,
  Close,
  PersonAdd,
  People,
  AdminPanelSettings,
  FolderShared,
} from "@mui/icons-material";
import { useStore } from "context/store/store";
import { useLayout } from "context/layout/layout";
import { UserService } from "services/users";
import { useNavigate } from "react-router-dom";
import { colors } from "utils/theme";

export function UsersList() {
  const store = useStore();
  const auth = useAuth();
  const layout = useLayout();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    axios
      .get(`${config.url}/users/showall`)
      .then((res) => {
        setUsers(res.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, []);

  const handleDelete = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    setLoading(true);
    try {
      await store.deleteUser({ user_id: userId });
      setUsers(users.filter((u) => u._id !== userId));
    } catch (err) {
      console.log(err);
      layout.errorAlert({ message: "Error al eliminar usuario" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditDrive = (user) => {
    setEditingId(user._id);
    setEditValue(user.gdriveFolderId || "");
  };

  const handleSaveDrive = async (userId) => {
    try {
      setLoading(true);
      await UserService.edit(userId, { gdriveFolderId: editValue });
      setUsers(
        users.map((u) =>
          u._id === userId ? { ...u, gdriveFolderId: editValue } : u
        )
      );
      setEditingId(null);
      layout.successAlert({ message: "Google Drive ID actualizado" });
    } catch (err) {
      console.log(err);
      layout.errorAlert({ message: "Error al actualizar" });
    } finally {
      setLoading(false);
    }
  };

  const totalUsers = users?.length || 0;
  const admins = users?.filter((u) => u.role?.toLowerCase() === "superadmin").length || 0;
  const withDrive = users?.filter((u) => u.gdriveFolderId).length || 0;

  const StatCard = ({ value, label, icon: Icon, accent }) => (
    <div
      style={{
        background: "#fff",
        borderRadius: "0.75rem",
        padding: "1.25rem 1.5rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid #f1f5f9",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div>
        <p style={{ fontSize: "1.75rem", fontWeight: 700, color: "#0f172a", margin: 0, lineHeight: 1.2 }}>{value}</p>
        <p style={{ fontSize: "0.8rem", color: "#64748b", margin: 0, marginTop: "0.25rem", fontWeight: 500 }}>{label}</p>
      </div>
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "0.625rem",
          background: accent + "12",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon sx={{ fontSize: 22, color: accent }} />
      </div>
    </div>
  );

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getRoleStyle = (role) => {
    if (role?.toLowerCase() === "superadmin") {
      return { bg: "#eff6ff", color: "#1d4ed8" };
    }
    return { bg: "#f1f5f9", color: "#475569" };
  };

  return (
    <>
      {loading && <Spinner />}
      <Sidebar />

      <div style={{ marginLeft: 210, minHeight: "100vh", background: "#f8fafc" }}>
        {/* Header */}
        <div
          style={{
            padding: "1.75rem 2rem 1.5rem",
            borderBottom: "1px solid #e2e8f0",
            background: "#fff",
          }}
        >
          <div style={{ maxWidth: 1400, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <p style={{ fontSize: "0.75rem", fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em", margin: 0, marginBottom: "0.25rem" }}>
                Administracion
              </p>
              <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#0f172a", margin: 0 }}>
                Usuarios
              </h1>
            </div>
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAdd sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/users/add")}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                borderRadius: "0.5rem",
                padding: "0.5rem 1.25rem",
                backgroundColor: colors.sapphire,
                boxShadow: "0 2px 8px rgba(3,83,164,0.2)",
                "&:hover": { backgroundColor: colors.oxfordBlue },
              }}
            >
              Agregar usuario
            </Button>
          </div>
        </div>

        <div style={{ maxWidth: 1400, margin: "0 auto", padding: "1.5rem 2rem" }}>
          {/* Stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
            <StatCard value={totalUsers} label="Total usuarios" icon={People} accent={colors.sapphire} />
            <StatCard value={admins} label="Administradores" icon={AdminPanelSettings} accent="#7c3aed" />
            <StatCard value={withDrive} label="Con Google Drive" icon={FolderShared} accent="#16a34a" />
          </div>

          {/* Table */}
          <div
            style={{
              background: "#fff",
              borderRadius: "0.75rem",
              border: "1px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Usuario", "Rol", "Google Drive Folder", ""].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          backgroundColor: "#f8fafc",
                          color: "#475569",
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          borderBottom: "1px solid #e2e8f0",
                          padding: "0.75rem 1rem",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users &&
                    users.map((user) => {
                      const roleStyle = getRoleStyle(user.role);
                      return (
                        <TableRow
                          key={user._id}
                          sx={{
                            transition: "background 0.1s",
                            "&:hover": { backgroundColor: "#f8fafc" },
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          {/* User info */}
                          <TableCell sx={{ padding: "0.75rem 1rem" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              <Avatar
                                sx={{
                                  width: 36,
                                  height: 36,
                                  fontSize: "0.8rem",
                                  fontWeight: 700,
                                  backgroundColor: colors.sapphire + "18",
                                  color: colors.sapphire,
                                }}
                              >
                                {getInitials(user.name)}
                              </Avatar>
                              <div>
                                <p style={{ margin: 0, fontWeight: 600, fontSize: "0.85rem", color: "#0f172a" }}>
                                  {user.name}
                                </p>
                                <p style={{ margin: 0, fontSize: "0.75rem", color: "#94a3b8" }}>
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </TableCell>

                          {/* Role */}
                          <TableCell sx={{ padding: "0.75rem 1rem" }}>
                            <Chip
                              label={user.role || "—"}
                              size="small"
                              sx={{
                                backgroundColor: roleStyle.bg,
                                color: roleStyle.color,
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 24,
                              }}
                            />
                          </TableCell>

                          {/* Google Drive */}
                          <TableCell sx={{ padding: "0.75rem 1rem" }}>
                            {editingId === user._id ? (
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <TextField
                                  size="small"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  placeholder="Pegar Google Drive Folder ID..."
                                  sx={{
                                    width: 300,
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0.5rem",
                                      fontSize: "0.8rem",
                                      height: 34,
                                      "& fieldset": { borderColor: "#e2e8f0" },
                                      "&.Mui-focused fieldset": { borderColor: colors.sapphire },
                                    },
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSaveDrive(user._id);
                                    if (e.key === "Escape") setEditingId(null);
                                  }}
                                  autoFocus
                                />
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveDrive(user._id)}
                                  sx={{
                                    backgroundColor: "#dcfce7",
                                    "&:hover": { backgroundColor: "#bbf7d0" },
                                    width: 30,
                                    height: 30,
                                  }}
                                >
                                  <Save sx={{ fontSize: 16, color: "#16a34a" }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => setEditingId(null)}
                                  sx={{
                                    backgroundColor: "#f1f5f9",
                                    "&:hover": { backgroundColor: "#e2e8f0" },
                                    width: 30,
                                    height: 30,
                                  }}
                                >
                                  <Close sx={{ fontSize: 16, color: "#64748b" }} />
                                </IconButton>
                              </div>
                            ) : (
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {user.gdriveFolderId ? (
                                  <Tooltip title={`ID: ${user.gdriveFolderId}`} arrow>
                                    <Chip
                                      icon={<FolderShared sx={{ fontSize: 14 }} />}
                                      label={user.gdriveFolderId.slice(0, 16) + "..."}
                                      size="small"
                                      onClick={() => handleEditDrive(user)}
                                      sx={{
                                        backgroundColor: "#dcfce7",
                                        color: "#15803d",
                                        fontWeight: 500,
                                        fontSize: "0.7rem",
                                        height: 26,
                                        cursor: "pointer",
                                        fontFamily: "monospace",
                                        "& .MuiChip-icon": { color: "#16a34a" },
                                        "&:hover": { backgroundColor: "#bbf7d0" },
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Chip
                                    label="Sin carpeta"
                                    size="small"
                                    onClick={() => handleEditDrive(user)}
                                    sx={{
                                      backgroundColor: "#f1f5f9",
                                      color: "#94a3b8",
                                      fontWeight: 500,
                                      fontSize: "0.7rem",
                                      height: 26,
                                      cursor: "pointer",
                                      "&:hover": { backgroundColor: "#e2e8f0", color: "#64748b" },
                                    }}
                                  />
                                )}
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditDrive(user)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    color: "#94a3b8",
                                    "&:hover": { color: colors.sapphire, backgroundColor: colors.sapphire + "10" },
                                  }}
                                >
                                  <Edit sx={{ fontSize: 15 }} />
                                </IconButton>
                              </div>
                            )}
                          </TableCell>

                          {/* Actions */}
                          <TableCell sx={{ padding: "0.75rem 1rem", width: 60 }}>
                            {auth.isAdmin && (
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(user._id)}
                                sx={{
                                  width: 30,
                                  height: 30,
                                  color: "#d1d5db",
                                  "&:hover": { color: "#ef4444", backgroundColor: "#fef2f2" },
                                }}
                              >
                                <Delete sx={{ fontSize: 17 }} />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            {users && users.length === 0 && !loading && (
              <div style={{ padding: "3rem", textAlign: "center" }}>
                <People sx={{ fontSize: 48, color: "#d1d5db", marginBottom: "0.75rem" }} />
                <p style={{ color: "#64748b", fontWeight: 600, marginBottom: "0.25rem" }}>No hay usuarios</p>
                <p style={{ color: "#94a3b8", fontSize: "0.85rem", marginBottom: "1.5rem" }}>Crea el primer usuario del sistema</p>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PersonAdd />}
                  onClick={() => navigate("/users/add")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "0.5rem",
                    backgroundColor: colors.sapphire,
                    "&:hover": { backgroundColor: colors.oxfordBlue },
                  }}
                >
                  Agregar usuario
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
